/**
 * SVI (Stochastic Volatility Inspired) Model Utilities
 *
 * SVI-Raw Formula:
 *   w(k) = a + b * [rho * (k - m) + sqrt((k - m)^2 + sigma^2)]
 *
 * Where:
 *   k = ln(K/F) is log-moneyness
 *   w is total variance = IV^2 * T
 *   IV = sqrt(w / T)
 *
 * The 'a' parameter is derived by pinning to ATM strike:
 *   a = w_pin - b * [rho * (k_pin - m) + sqrt((k_pin - m)^2 + sigma^2)]
 */

/**
 * Compute SVI Raw total variance w(k)
 */
export function sviRawVariance(k, a, b, rho, m, sigma) {
  const dk = k - m;
  return a + b * (rho * dk + Math.sqrt(dk * dk + sigma * sigma));
}

/**
 * Compute 'a' parameter from pinning constraint
 *
 * @param {number} kPin - Log-moneyness of pinned strike: ln(pinStrike / forward)
 * @param {number} wPin - Market variance at pinned strike: ivPin^2 * T
 * @param {number} b - Variance scale
 * @param {number} rho - Skew (-1 to 1)
 * @param {number} m - Horizontal shift
 * @param {number} sigma - Curvature
 */
export function computeAFromPin(kPin, wPin, b, rho, m, sigma) {
  const dkPin = kPin - m;
  return wPin - b * (rho * dkPin + Math.sqrt(dkPin * dkPin + sigma * sigma));
}

/**
 * Convert SVI variance to implied volatility
 */
export function varianceToIV(w, T) {
  return Math.sqrt(Math.max(w, 1e-10) / T);
}

/**
 * Generate smooth SVI curve points
 *
 * @param {Object} chainData - Option chain data from API
 * @param {number} numPoints - Number of points for smooth curve (default: 100)
 * @param {number} moneynessRange - Range of moneyness to plot (default: 0.3 = +/-30%)
 * @returns {Array} Array of {moneyness, fittedIV} points
 */
export function generateSVICurve(chainData, numPoints = 100, moneynessRange = 0.3) {
  if (!chainData || !chainData.svi_params || !chainData.forward_price || !chainData.time_to_expiry) {
    return null;
  }

  const { b, rho, m, sigma, pin_strike, atm_iv } = chainData.svi_params;
  const forward = chainData.forward_price;
  const T = chainData.time_to_expiry;

  // Validate we have all required params
  if (b == null || rho == null || m == null || sigma == null || !forward || !T) {
    return null;
  }

  // Compute 'a' from pinning
  // k_pin = ln(pin_strike / forward)
  // w_pin = atm_iv^2 * T (using ATM IV as proxy for pin IV)
  const kPin = pin_strike ? Math.log(pin_strike / forward) : 0;
  const wPin = atm_iv ? (atm_iv * atm_iv * T) : 0;

  // If we don't have pin data, try to derive from ATM
  const a = computeAFromPin(kPin, wPin, b, rho, m, sigma);

  // Generate points across moneyness range
  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    // Moneyness from (1 - range) to (1 + range), e.g., 0.7 to 1.3
    const moneyness = (1 - moneynessRange) + (2 * moneynessRange * i / numPoints);
    const k = Math.log(moneyness); // k = ln(K/F), so K/F = moneyness means k = ln(moneyness)

    const w = sviRawVariance(k, a, b, rho, m, sigma);
    const iv = varianceToIV(w, T) * 100; // Convert to percentage

    points.push({
      moneyness: parseFloat(moneyness.toFixed(4)),
      fittedIV: parseFloat(iv.toFixed(2)),
      strike: forward * moneyness, // Approximate strike
    });
  }

  return points;
}

/**
 * Generate SVI curve for comparison chart (both crypto and ETF)
 *
 * @param {Object} cryptoChain - Crypto option chain data
 * @param {Object} etfChain - ETF option chain data
 * @param {number} conversionRatio - Price ratio for strike normalization
 * @param {number} numPoints - Number of points
 * @param {number} minBound - Optional min moneyness bound
 * @param {number} maxBound - Optional max moneyness bound
 */
export function generateComparisonCurves(cryptoChain, etfChain, conversionRatio, numPoints = 100, minBound = null, maxBound = null) {
  const data = [];

  // Use provided bounds or calculate from data
  let minMoneyness = minBound;
  let maxMoneyness = maxBound;

  if (minMoneyness === null || maxMoneyness === null) {
    minMoneyness = 0.7;
    maxMoneyness = 1.3;

    if (cryptoChain?.strikes && cryptoChain.forward_price) {
      const cryptoMoneynesses = cryptoChain.strikes.map(k => k / cryptoChain.forward_price);
      minMoneyness = Math.min(minMoneyness, Math.min(...cryptoMoneynesses));
      maxMoneyness = Math.max(maxMoneyness, Math.max(...cryptoMoneynesses));
    }

    if (etfChain?.strikes && etfChain.forward_price && conversionRatio) {
      const etfMoneynesses = etfChain.strikes.map(k => (k * conversionRatio) / (cryptoChain?.forward_price || etfChain.forward_price * conversionRatio));
      minMoneyness = Math.min(minMoneyness, Math.min(...etfMoneynesses));
      maxMoneyness = Math.max(maxMoneyness, Math.max(...etfMoneynesses));
    }

    // Add some padding
    minMoneyness = Math.max(0.5, minMoneyness - 0.05);
    maxMoneyness = Math.min(1.5, maxMoneyness + 0.05);
  }

  // Generate crypto SVI curve
  const cryptoCurve = cryptoChain ? generateSVICurveAtMoneyness(cryptoChain, minMoneyness, maxMoneyness, numPoints) : null;

  // Generate ETF SVI curve
  const etfCurve = etfChain ? generateSVICurveAtMoneyness(etfChain, minMoneyness, maxMoneyness, numPoints) : null;

  // Merge into single dataset
  for (let i = 0; i <= numPoints; i++) {
    const moneyness = minMoneyness + (maxMoneyness - minMoneyness) * i / numPoints;
    const point = { moneyness: parseFloat(moneyness.toFixed(4)) };

    if (cryptoCurve && cryptoCurve[i]) {
      point.cryptoFittedIV = cryptoCurve[i].fittedIV;
    }
    if (etfCurve && etfCurve[i]) {
      point.etfFittedIV = etfCurve[i].fittedIV;
    }

    data.push(point);
  }

  return data;
}

/**
 * Generate SVI curve at specific moneyness range
 */
function generateSVICurveAtMoneyness(chainData, minMoneyness, maxMoneyness, numPoints) {
  if (!chainData?.svi_params || !chainData.forward_price || !chainData.time_to_expiry) {
    return null;
  }

  const { b, rho, m, sigma, pin_strike, atm_iv } = chainData.svi_params;
  const forward = chainData.forward_price;
  const T = chainData.time_to_expiry;

  if (b == null || rho == null || m == null || sigma == null || !T) {
    return null;
  }

  // Compute 'a' from pinning
  const kPin = pin_strike ? Math.log(pin_strike / forward) : 0;
  const wPin = atm_iv ? (atm_iv * atm_iv * T) : 0;
  const a = computeAFromPin(kPin, wPin, b, rho, m, sigma);

  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    const moneyness = minMoneyness + (maxMoneyness - minMoneyness) * i / numPoints;
    const k = Math.log(moneyness);

    const w = sviRawVariance(k, a, b, rho, m, sigma);

    // Skip invalid variances
    if (w < 0) {
      points.push({ moneyness, fittedIV: null });
      continue;
    }

    const iv = varianceToIV(w, T) * 100;

    points.push({
      moneyness: parseFloat(moneyness.toFixed(4)),
      fittedIV: parseFloat(iv.toFixed(2)),
    });
  }

  return points;
}

export default {
  sviRawVariance,
  computeAFromPin,
  varianceToIV,
  generateSVICurve,
  generateComparisonCurves,
};
